import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, Users, Shield, Sparkles, MessageCircle,
  ArrowRight, Search, Zap
} from 'lucide-react';
import PageBackground from '@components/PageBackground';
import Footer from '@components/Footer';
import SeoHead, { organizationJsonLd, websiteJsonLd } from '@components/SeoHead';
import { DEFAULT_DESCRIPTION } from '@config/site';

const faqs = [
  {
    question: 'How does RoomZa roommate matching work?',
    answer:
      'Create a lifestyle profile, swipe on compatible people, and chat in-app. Our AI scores compatibility across habits, budget, and living preferences so you can match before you move in.',
  },
  {
    question: 'Where is RoomZa available?',
    answer:
      'RoomZa is launching across South Africa. Create a profile for Cape Town, Johannesburg, Durban, Pretoria, or wherever you are looking — the community is just getting started.',
  },
  {
    question: 'Can landlords list properties on RoomZa?',
    answer:
      'Yes. Landlords can list homes and receive inquiries from roommate seekers whose budgets and preferences already fit the property.',
  },
  {
    question: 'Is RoomZa free to use?',
    answer:
      'Yes — signing up, matching with roommates, and listing a property are free while we grow. If paid plans are introduced later, pricing will be shown clearly before you commit.',
  },
];

const landingJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    organizationJsonLd(),
    websiteJsonLd(),
    {
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ],
};

const Landing: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-pink-200 selection:text-pink-900 scroll-smooth">
      <SeoHead
        path="/"
        description={DEFAULT_DESCRIPTION}
        jsonLd={landingJsonLd}
      />
      
      {/* --- Ambient Background --- */}
      <PageBackground />

      {/* --- Navigation --- */}
      <nav className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl border-b border-gray-200/50" aria-label="Primary">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="RoomZa" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold tracking-tight text-gray-900">RoomZa</span>
          </Link>

          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center text-xs font-mono text-gray-500 bg-gray-50 px-3 py-1 rounded border border-gray-200">
              <span className="relative flex h-2 w-2 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              NOW LIVE IN SOUTH AFRICA
            </div>
            <Link
              to="/how-it-works"
              className="hidden sm:inline text-sm font-semibold text-gray-900 hover:text-pink-600 transition-colors"
            >
              How it works
            </Link>
            <Link 
              to="/login"
              className="text-sm font-semibold text-gray-900 hover:text-pink-600 transition-colors"
            >
              Log in
            </Link>
            <Link 
              to="/signup?role=roommate_seeker"
              className="bg-gray-900 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-transform hover:-translate-y-0.5 shadow-lg shadow-gray-200"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      <main className="relative z-10 pt-32 pb-20 max-w-[1400px] mx-auto px-4 sm:px-6">
        
        {/* --- Hero Section --- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-24 min-h-[80vh]">
          
          {/* Main Headline Area */}
          <div className="lg:col-span-8 flex flex-col justify-center lg:pr-12">
            <div className="inline-flex items-center space-x-2 mb-8">
              <span className="px-3 py-1 rounded-full border border-pink-200 bg-pink-50 text-pink-600 text-xs font-bold tracking-wider uppercase">
                Early access
              </span>
              <span className="text-sm text-gray-500">AI roommate matching for South Africa</span>
            </div>
            
            <h1 className="text-6xl md:text-8xl font-bold tracking-tighter text-gray-900 mb-8 leading-[0.9]">
              Find your <br />
              {/* Text Gradient updated to Pink/Rose */}
              <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600 bg-clip-text text-transparent">
                perfect roommate.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-2xl leading-relaxed mb-10 font-light">
              Swipe, match, and connect with compatible roommates in Cape Town, Johannesburg, Durban, and across South Africa.{' '}
              <span className="font-medium text-pink-600">Then discover your dream home together.</span>
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              {/* Primary Button: Pink Gradient */}
              <Link 
                to="/signup?role=roommate_seeker"
                className="w-full sm:w-auto h-14 px-8 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold text-lg hover:shadow-xl hover:shadow-pink-200 hover:scale-105 transition-all flex items-center justify-center group"
              >
                Find a Roommate
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              {/* Secondary Button: Pink Border */}
              <Link 
                to="/signup?role=landlord"
                className="w-full sm:w-auto h-14 px-8 rounded-xl border-2 border-pink-100 bg-white text-pink-600 font-bold text-lg hover:border-pink-200 hover:bg-pink-50 transition-all flex items-center justify-center"
              >
                I'm a Landlord
              </Link>
            </div>
            
            <div className="mt-8 flex flex-wrap items-center gap-3 text-sm font-medium text-gray-600">
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-pink-100 px-3 py-1.5 shadow-sm">
                <Sparkles className="w-4 h-4 text-pink-500" />
                AI compatibility scoring
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-pink-100 px-3 py-1.5 shadow-sm">
                <Shield className="w-4 h-4 text-pink-500" />
                Chat in-app first
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-pink-100 px-3 py-1.5 shadow-sm">
                <Users className="w-4 h-4 text-pink-500" />
                Free to join
              </span>
            </div>
          </div>

          {/* Right Column: Visual Bento Grid */}
          <div className="lg:col-span-4 grid grid-rows-3 gap-4 h-full min-h-[500px]">
            {/* Card 1: Product preview */}
            <div className="row-span-2 bg-white/60 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-xl shadow-pink-100/50 flex flex-col justify-between relative overflow-hidden group hover:scale-[1.02] transition-transform duration-500">
              <div className="absolute top-0 right-0 w-32 h-32 bg-pink-200/50 rounded-full blur-2xl -mr-10 -mt-10"></div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-pink-500 mb-3">Example match preview</p>
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-4">
                  <Heart className="w-6 h-6 text-pink-500 fill-pink-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Compatibility score</h3>
                <p className="text-gray-500 mt-2">Sleep schedule, cleanliness, budget, and more — scored before you chat.</p>
              </div>
              
              <div className="mt-8 space-y-3">
                <div className="flex items-center p-3 bg-white/80 rounded-xl border border-white/50 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 mr-3 overflow-hidden">
                    <img src="/landing/match1.png" alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">Cape Town</div>
                    <div className="text-xs text-gray-500">Lifestyle + budget match</div>
                  </div>
                </div>
                <div className="flex items-center p-3 bg-white/80 rounded-xl border border-white/50 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 mr-3 overflow-hidden">
                    <img src="/landing/match2.png" alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">Johannesburg</div>
                    <div className="text-xs text-gray-500">Shared-home ready</div>
                  </div>
                </div>
                <div className="flex items-center p-3 bg-white/80 rounded-xl border border-white/50 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 mr-3 overflow-hidden">
                    <img src="/landing/match1.png" alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">Durban</div>
                    <div className="text-xs text-gray-500">Swipe · match · move in</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Product signal */}
            <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-3xl p-6 text-white flex flex-col justify-center relative overflow-hidden group hover:scale-[1.02] transition-transform duration-500">
              <Sparkles className="w-6 h-6 text-pink-200 absolute top-6 right-6 animate-spin-slow" />
              <div className="text-4xl font-bold mb-1">50+</div>
              <div className="text-pink-100 text-sm font-medium">Lifestyle signals used to score compatibility</div>
            </div>
          </div>
        </div>

        {/* --- "The Problem" Section --- */}
        <div className="border-t border-gray-200 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-sm font-mono text-gray-500 mb-4 uppercase tracking-widest">The Old Way</h2>
              <p className="text-3xl md:text-4xl font-light text-gray-400 leading-tight">
                Scroll through endless forums. Send emails into the void. Meet strangers who don't match. <span className="line-through decoration-pink-300">Hope for the best.</span>
              </p>
            </div>
            <div>
              <h2 className="text-sm font-mono text-pink-600 mb-4 uppercase tracking-widest">The RoomZa Way</h2>
              <p className="text-3xl md:text-4xl font-medium text-gray-900 leading-tight">
                Lifestyle matching. In-app chat. Shared budget planning. Homes that fit both of you.{' '}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-rose-600">Compatibility first.</span>
              </p>
            </div>
          </div>
        </div>

        {/* --- Feature Bento Grid --- */}
        <div className="py-20">
          <div className="flex justify-between items-end mb-12">
            <h2 className="text-5xl font-bold tracking-tight text-gray-900">Intelligence <br/> at work.</h2>
            <Link 
              to="/how-it-works"
              className="hidden md:flex items-center text-gray-900 font-semibold border-b-2 border-gray-900 pb-1 hover:text-pink-600 hover:border-pink-600 transition-all"
            >
              See how it works <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[minmax(300px,auto)]">
            
            {/* Feature 1: Instant Synergy (Updated Blue/Purple to Rose/Pink) */}
            <div className="md:col-span-2 bg-white rounded-[2rem] p-10 border border-gray-100 shadow-xl shadow-pink-200/20 flex flex-col md:flex-row items-center overflow-hidden group">
              <div className="flex-1 z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-600">
                    <Zap className="w-7 h-7" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Instant Synergy</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our AI analyzes 50+ data points including lifestyle habits, social battery, and cleanliness standards.
                </p>
              </div>
              <div className="flex-1 w-full mt-8 md:mt-0 relative h-64 md:h-full flex items-center justify-center">
                 {/* Abstract UI representation */}
                 <div className="relative w-64 h-40 bg-white border border-gray-200 rounded-xl shadow-lg transform rotate-3 transition-transform group-hover:rotate-0 duration-500 flex flex-col p-4">
                    <div className="h-2 w-20 bg-gray-200 rounded mb-4"></div>
                    <div className="flex gap-2">
                       <div className="h-20 w-1/2 bg-rose-100 rounded-lg overflow-hidden">
                         <img src="/landing/match1.png" alt="Match 1" className="w-full h-full object-cover" />
                       </div>
                       <div className="h-20 w-1/2 bg-pink-100 rounded-lg overflow-hidden">
                         <img src="/landing/match2.png" alt="Match 2" className="w-full h-full object-cover" />
                       </div>
                    </div>
                    <div className="absolute -right-4 -top-4 bg-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      HIGH MATCH
                    </div>
                 </div>
              </div>
            </div>

            {/* Feature 2: Safer by design */}
            <div className="bg-gray-900 rounded-[2rem] p-10 text-white flex flex-col justify-between group overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-pink-600/30 rounded-full blur-[80px] -mr-16 -mt-16 group-hover:bg-pink-600/50 transition-colors duration-500"></div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-10 h-10 text-pink-300" />
                  <h3 className="text-2xl font-bold">Safer by design</h3>
                </div>
                <p className="text-gray-400">Chat inside RoomZa first. Share personal details only when you are ready.</p>
              </div>
              <div className="mt-8 flex items-center gap-2 text-sm font-mono text-pink-300">
                 <Shield className="w-4 h-4" /> IN-APP CHAT FIRST
              </div>
            </div>

            {/* Feature 3: Safe Chat (Pink Bubbles) */}
            <div className="bg-white rounded-[2rem] p-10 border border-gray-100 shadow-xl shadow-pink-200/20 group hover:border-pink-200 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 bg-pink-50 rounded-2xl flex items-center justify-center text-pink-600">
                  <MessageCircle className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Safe Chat</h3>
              </div>
              <p className="text-gray-600 mb-6">Connect without sharing your phone number until you're ready.</p>
              <div className="space-y-2">
                <div className="bg-gray-50 p-3 rounded-lg rounded-tl-none text-sm text-gray-600 max-w-[80%]">Hi! Saw we both love jazz?</div>
                <div className="bg-pink-50 p-3 rounded-lg rounded-tr-none text-sm text-pink-900 ml-auto max-w-[80%]">Yes! Do you play anything?</div>
              </div>
            </div>

            {/* Feature 4: Discover Homes */}
            <div className="md:col-span-2 bg-gradient-to-r from-rose-50 to-pink-50 rounded-[2rem] p-10 border border-white/50 flex flex-col md:flex-row items-center justify-between">
              <div className="max-w-md">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Discover Homes Together</h3>
                <p className="text-lg text-gray-600 mb-8">
                  Found a match? Browse apartments that fit your combined budget and preferences instantly.
                </p>
                <Link
                  to="/signup?role=roommate_seeker"
                  className="bg-white text-gray-900 px-6 py-3 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all inline-flex items-center"
                >
                  <Search className="w-4 h-4 mr-2" /> Browse Listings
                </Link>
              </div>
              <div className="mt-8 md:mt-0 flex gap-4">
                 <div className="bg-white p-4 rounded-2xl shadow-sm rotate-[-6deg] w-48">
                    <div className="h-24 bg-gray-200 rounded-xl mb-3 overflow-hidden">
                        <img src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=400&q=80" alt="Room" className="w-full h-full object-cover" />
                    </div>
                    <div className="h-4 w-3/4 bg-gray-100 rounded mb-2"></div>
                 </div>
                 <div className="bg-white p-4 rounded-2xl shadow-sm rotate-[6deg] w-48 hidden sm:block">
                    <div className="h-24 bg-gray-200 rounded-xl mb-3 overflow-hidden">
                        <img src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=400&q=80" alt="Room" className="w-full h-full object-cover" />
                    </div>
                    <div className="h-4 w-3/4 bg-gray-100 rounded mb-2"></div>
                 </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- Landlord Section --- */}
        <div className="py-20 border-t border-gray-200">
          <div className="text-center mb-16">
            <span className="px-3 py-1 rounded-full border border-pink-200 bg-pink-50 text-pink-600 text-xs font-bold tracking-wider uppercase">
              For Property Owners
            </span>
            <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-gray-900 mt-6 mb-6">
              Fill vacancies faster.<br/>
              <span className="bg-gradient-to-r from-pink-500 to-rose-600 bg-clip-text text-transparent">
                With better tenants.
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              List your place and connect with roommate seekers whose lifestyle and budget already fit — so you spend less time chasing the wrong leads.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Compatibility first</h3>
              <p className="text-gray-600 leading-relaxed">
                Seekers match with each other before they inquire, so groups arrive more aligned on lifestyle and budget.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Clear profiles</h3>
              <p className="text-gray-600 leading-relaxed">
                Review lifestyle details, preferences, and chat history in one place before you schedule a viewing.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Focused inquiries</h3>
              <p className="text-gray-600 leading-relaxed">
                Hear from people who already match your property&apos;s budget and requirements — not random inbox spam.
              </p>
            </div>
          </div>

          {/* How It Works for Landlords */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2rem] p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-pink-600/20 rounded-full blur-[120px]"></div>
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center">How it works for landlords</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8">
                {/* Step 1 */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-pink-500 rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    1
                  </div>
                  <h4 className="text-lg font-bold mb-2">List Your Property</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Add photos, set budget range, and specify requirements in minutes.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-pink-500 rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    2
                  </div>
                  <h4 className="text-lg font-bold mb-2">Get Matched</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Our AI surfaces your listing to compatible tenant groups automatically.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-pink-500 rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    3
                  </div>
                  <h4 className="text-lg font-bold mb-2">Review Inquiries</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Chat with interested groups, review profiles, and schedule viewings.
                  </p>
                </div>

                {/* Step 4 */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-pink-500 rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    4
                  </div>
                  <h4 className="text-lg font-bold mb-2">Lease Signed</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Finalize terms with compatible tenants who are ready to move in.
                  </p>
                </div>
              </div>

              <div className="mt-12 text-center">
                <Link 
                  to="/signup?role=landlord"
                  className="bg-white text-gray-900 px-8 py-4 rounded-xl font-bold text-lg hover:scale-105 transition-transform shadow-xl inline-flex items-center gap-2"
                >
                  List Your Property
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <p className="text-gray-400 text-sm mt-4">Free to list while we grow</p>
              </div>
            </div>
          </div>
        </div>

        {/* --- FAQ (search / featured-snippet friendly) --- */}
        <section className="py-20 border-t border-gray-200" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="faq-heading" className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4 text-center">
              Roommate matching, answered
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Everything people ask before finding a flatmate or listing a place on RoomZa.
            </p>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="group bg-white rounded-2xl border border-gray-100 shadow-sm open:shadow-md transition-shadow"
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 p-6 font-semibold text-gray-900">
                    {faq.question}
                    <span className="text-pink-500 text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="px-6 pb-6 text-gray-600 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* --- Footer CTA (Dark but with Pink Glows) --- */}
        <div className="mt-20 rounded-[2.5rem] bg-gray-900 text-white overflow-hidden relative">
           <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
           <div className="absolute -top-40 -left-40 w-96 h-96 bg-pink-600 rounded-full mix-blend-screen filter blur-[100px] opacity-30"></div>
           <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-rose-600 rounded-full mix-blend-screen filter blur-[100px] opacity-30"></div>
           
           <div className="relative z-10 px-8 py-24 text-center max-w-3xl mx-auto">
             <h2 className="text-5xl md:text-7xl font-bold tracking-tighter mb-8">
               Your space.<br/>Your people.
             </h2>
             <p className="text-xl text-gray-400 mb-10 font-light">
               Be among the first roommates and landlords building RoomZa across South Africa.
             </p>
             <Link 
                to="/signup?role=roommate_seeker"
                className="bg-gradient-to-r from-pink-500 to-rose-600 text-white px-10 py-4 rounded-full text-lg font-bold hover:scale-105 transition-transform duration-300 shadow-xl shadow-pink-900/50 inline-block"
             >
               Join RoomZa early
             </Link>
           </div>
        </div>

        {/* --- Footer --- */}
        <Footer />

      </main>
    </div>
  );
};

export default Landing;